import { useCallback } from 'react';
import { ensureEditableCaretForInsert, tryDeleteAdjacentAtomic } from '../../../utils/atomicCaret';
import { sanitizeClipboardHtml } from '../../../utils/pasteUtils';
import { escapeHtml } from '../../../utils/pasteUtils/htmlEscape';
import { separateNextUndoStep } from '../../../utils/undoSeparation';
import { convertBraceVarsToPills } from '../../../utils/variableUtils';
import { canonicalizeCodeHtml, isEmptyCodeContent } from '../utils/codeContent';
import { insertCodeHtmlAtSelection } from '../utils/insertCodeHtml';

/**
 * Paste + keydown handlers for the code editable (Enter/Tab/Backspace/vars).
 */
export const useCodeBlockInput = ({
  contentEditableRef,
  contentRef,
  commitContent,
  tryOpenFromBraceKey,
  onDelete,
  blockId,
}) => {
  /** Paste into code: plain text (+ pills), not rich markdown formatting. */
  const handleCodePaste = useCallback(
    (e) => {
      const clipboard = e.clipboardData;
      if (!clipboard) return;
      const html = clipboard.getData('text/html') || '';
      if (html.includes('data-doc-block')) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      let sanitized;
      if (/data-doc-variable|data-doc-math/i.test(html)) {
        // Shared sanitizer joins lines with `<br>` — code uses real newlines.
        sanitized = canonicalizeCodeHtml(sanitizeClipboardHtml(html));
      } else {
        const plain = clipboard.getData('text/plain') || '';
        sanitized = escapeHtml(plain).replace(/\r\n?/g, '\n');
      }
      sanitized = convertBraceVarsToPills(sanitized);
      if (!sanitized) return;
      const el = e.currentTarget;
      if (!el) return;
      separateNextUndoStep();
      ensureEditableCaretForInsert();
      insertCodeHtmlAtSelection(el, sanitized);
      commitContent(el.innerHTML);
    },
    [commitContent]
  );

  const handleKeyDown = (e) => {
    // Keep keystrokes inside the code block (the editor listens document-wide)
    e.stopPropagation();

    if (tryOpenFromBraceKey(e)) return;

    if (tryDeleteAdjacentAtomic(e)) {
      const el = contentEditableRef.current;
      if (el) commitContent(el.innerHTML);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      // Real `\n` (not `<br>`) so large code stays a light DOM.
      const el = contentEditableRef.current;
      if (!el) return;
      if (!document.execCommand('insertText', false, '\n')) {
        const sel = window.getSelection();
        if (sel?.rangeCount) {
          const range = sel.getRangeAt(0);
          range.deleteContents();
          const textNode = document.createTextNode('\n');
          range.insertNode(textNode);
          range.setStartAfter(textNode);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
      commitContent(el.innerHTML);
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      document.execCommand('insertText', false, '  ');
      const el = contentEditableRef.current;
      if (el) commitContent(el.innerHTML);
      return;
    }

    if (
      (e.key === 'Backspace' || e.key === 'Delete') &&
      isEmptyCodeContent(contentRef.current)
    ) {
      e.preventDefault();
      onDelete?.(blockId);
    }
  };

  return { handleCodePaste, handleKeyDown };
};
